import { Text } from '@/shared/ui/primitives';
import { memo, useMemo, useState } from 'react';

import { SectionLabel } from '@/shared/ui';
import { TEST_IDS, TEST_ID_BUILDERS } from '@/testIds';
import { ChevronLeftIcon } from '@/shared/ui/icons/ChevronLeftIcon';
import { ChevronRightIcon } from '@/shared/ui/icons/ChevronRightIcon';
import {
  addCalendarDays,
  calendarDateFromParts,
  compareCalendarDates,
  getCalendarWeekday,
  parseCalendarDateInput,
  type CalendarDate,
} from '@/shared/lib/calendarDate';

import styles from './InlineCalendar.module.css';

export type FocusedPill = 'start' | 'end';

export interface InlineCalendarProps {
  today: CalendarDate;
  startDate: string;
  endDate: string;
  isUnlimitedDuration: boolean;
  focused: FocusedPill;
  onDayClick: (date: CalendarDate) => void;
}

const WEEKDAY_LABELS = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];

const MONTH_LABEL_FORMATTER = new Intl.DateTimeFormat(undefined, {
  month: 'long',
  year: 'numeric',
});

interface ViewMonth {
  year: number;
  month: number;
}

const buildCells = ({ year, month }: ViewMonth): CalendarDate[] => {
  const firstOfMonth = calendarDateFromParts(year, month, 1);
  const gridStart = addCalendarDays(firstOfMonth, -getCalendarWeekday(firstOfMonth));
  return Array.from({ length: 42 }, (_, i) => addCalendarDays(gridStart, i));
};

const monthOf = (date: CalendarDate): ViewMonth => {
  const [y, m] = date.split('-');
  return { year: Number(y), month: Number(m) };
};

const isBetweenInclusive = (d: CalendarDate, a: CalendarDate, b: CalendarDate): boolean =>
  compareCalendarDates(d, a) >= 0 && compareCalendarDates(d, b) <= 0;

export const InlineCalendar = memo(function InlineCalendar({
  today,
  startDate,
  endDate,
  isUnlimitedDuration,
  focused,
  onDayClick,
}: InlineCalendarProps) {
  const parsedStart = parseCalendarDateInput(startDate);
  const parsedEnd = isUnlimitedDuration ? null : parseCalendarDateInput(endDate);

  const focusedDate = focused === 'start' ? parsedStart : (parsedEnd ?? parsedStart);

  const [view, setView] = useState<ViewMonth>(() => monthOf(focusedDate ?? today));

  const monthLabel = MONTH_LABEL_FORMATTER.format(new Date(view.year, view.month - 1, 1));

  const showRange = parsedStart && parsedEnd && compareCalendarDates(parsedStart, parsedEnd) <= 0;

  const cells = useMemo(() => buildCells(view), [view]);

  const inRangeFlags = cells.map((date) =>
    showRange && parsedStart && parsedEnd ? isBetweenInclusive(date, parsedStart, parsedEnd) : false
  );

  const goPrev = (): void => {
    setView((prev) =>
      prev.month === 1
        ? { year: prev.year - 1, month: 12 }
        : { year: prev.year, month: prev.month - 1 }
    );
  };

  const goNext = (): void => {
    setView((prev) =>
      prev.month === 12
        ? { year: prev.year + 1, month: 1 }
        : { year: prev.year, month: prev.month + 1 }
    );
  };

  return (
    <div className={styles.calendar}>
      <div className={styles.header}>
        <button
          type="button"
          className={styles.navButton}
          onClick={goPrev}
          aria-label="Previous month"
          data-testid={TEST_IDS.passCalendarPrevMonth}
        >
          <ChevronLeftIcon />
        </button>
        <SectionLabel className={styles.monthLabel} data-testid={TEST_IDS.passCalendarMonthLabel}>
          {monthLabel}
        </SectionLabel>
        <button
          type="button"
          className={styles.navButton}
          onClick={goNext}
          aria-label="Next month"
          data-testid={TEST_IDS.passCalendarNextMonth}
        >
          <ChevronRightIcon />
        </button>
      </div>

      <div className={styles.dow}>
        {WEEKDAY_LABELS.map((label, i) => (
          <Text variant="caption" key={i} weight="regular" className={styles.dowCell}>
            {label}
          </Text>
        ))}
      </div>

      <div className={styles.grid}>
        {cells.map((date, i) => {
          const cellMonth = monthOf(date);
          const isOffMonth = cellMonth.year !== view.year || cellMonth.month !== view.month;
          const isToday = date === today;
          const isStartCell = parsedStart === date;
          const isEndCell = parsedEnd === date;
          const isFocusedSelected = focused === 'start' ? isStartCell : isEndCell;
          const isUnfocusedSelected =
            focused === 'start' ? isEndCell && !isStartCell : isStartCell && !isEndCell;
          const isRange = inRangeFlags[i];
          const leftIn = i % 7 !== 0 && inRangeFlags[i - 1];
          const rightIn = i % 7 !== 6 && inRangeFlags[i + 1];
          const upIn = i >= 7 && inRangeFlags[i - 7];
          const downIn = i < cells.length - 7 && inRangeFlags[i + 7];

          const fillets = isRange
            ? []
            : [
                leftIn && upIn && styles.filletTL,
                rightIn && upIn && styles.filletTR,
                leftIn && downIn && styles.filletBL,
                rightIn && downIn && styles.filletBR,
              ].filter((c): c is string => Boolean(c));

          const className = [
            styles.cell,
            isOffMonth && styles.cellOffMonth,
            isRange && styles.cellInRange,
            isRange && !leftIn && !upIn && styles.cellRoundTL,
            isRange && !rightIn && !upIn && styles.cellRoundTR,
            isRange && !leftIn && !downIn && styles.cellRoundBL,
            isRange && !rightIn && !downIn && styles.cellRoundBR,
            isFocusedSelected && styles.cellFocused,
            isUnfocusedSelected && styles.cellUnfocused,
            isToday && styles.cellToday,
          ]
            .filter(Boolean)
            .join(' ');

          const dayNumber = Number(date.split('-')[2]);

          return (
            <button
              type="button"
              key={date}
              className={className}
              onClick={() => onDayClick(date)}
              data-testid={TEST_ID_BUILDERS.passCalendarDay(date)}
              aria-label={date}
              aria-pressed={isFocusedSelected || isUnfocusedSelected}
            >
              {fillets.map((cls) => (
                <span key={cls} aria-hidden="true" className={`${styles.fillet} ${cls}`} />
              ))}
              <span className={styles.cellNumber}>{dayNumber}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
});
